import { ref, toDisplayString } from "vue";
import turkish from "./tr";

/**
 * The panel's language: English (the words as written in the code) or Turkish (owner 2026-11-03). The choice is kept in
 * this browser and starts as English.
 *
 * Templates call $tr on their own (build/panel-i18n.cjs wraps their static words); text built in JavaScript calls
 * $tr / tr where it is written. tr follows `locale`, so every page re-renders when the top bar switches it.
 */
const KEY = "panel_lang";

export const locale = ref(localStorage.getItem(KEY) === "tr" ? "tr" : "en");

export function setLocale(lang) {
  locale.value = lang === "tr" ? "tr" : "en";
  localStorage.setItem(KEY, locale.value);
  document.documentElement.lang = locale.value;
}

/**
 * The words in the chosen language: the English as written, or its Turkish when the dictionary has it.
 * $context tells apart the same English used for two things: tr("Change", "price drift") looks up
 * "price drift::Change" first (a column header, "Değişim"), then "Change" (the button, "Değiştir").
 */
export function tr(text, context) {
  if (locale.value !== "tr" || typeof text !== "string" || text === "") return text;
  if (typeof context === "string" && context) {
    const inContext = turkish[context + "::" + text];
    if (inContext !== undefined) return inContext;
  }
  const found = turkish[text];
  if (found !== undefined) return found;
  // Text built in JavaScript may carry spaces at its ends; the dictionary keys do not.
  const trimmed = text.trim();
  const inner = trimmed !== text ? turkish[trimmed] : undefined;
  return inner === undefined ? text : text.replace(trimmed, inner);
}

/**
 * A sentence with values in it, translated whole: "Approved by {0}" with values ["Ali"] (Turkish "{0} tarafından
 * onaylandı" puts the value where Turkish needs it). Values show as a template would show them.
 */
export function trf(text, values = []) {
  return String(tr(text)).replace(/\{(\d+)\}/g, (all, i) => (i < values.length ? toDisplayString(values[i]) : all));
}

/**
 * The locale a date or time is shown in: Turkish ("14 Eki", "Salı") in Turkish, else the English locale the page
 * already used (toLocaleDateString("en-CA", …) → toLocaleDateString(dateLocale("en-CA"), …); none for the browser's).
 * Only for text people read: a date used as a key or sent to the API ("YYYY-MM-DD") keeps its fixed format.
 */
export function dateLocale(english) {
  return locale.value === "tr" ? "tr-TR" : english;
}

export default {
  install(app) {
    app.config.globalProperties.$tr = tr;
    app.config.globalProperties.$trf = trf;
    document.documentElement.lang = locale.value;
  },
};
